import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatDate } from "../lib/api";
import { Stars } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Bookmark, BadgeCheck, MapPin, Calendar, Users, Globe, Send } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function VendorProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [v, setV] = useState(null);

  useEffect(() => {
    api.get(`/vendors/${id}`).then((r) => setV(r.data)).catch(() => setV(false));
  }, [id]);

  const toggleFav = async () => {
    try { await api.post(`/vendors/${id}/favorite`); await refreshUser(); toast.success("Updated favourites"); }
    catch { toast.error("Could not update"); }
  };

  if (v === null) return <Skeleton className="h-96" />;
  if (v === false) return <div className="text-center py-12 text-zinc-500">Vendor not found</div>;

  const fav = (user?.favorites || []).includes(id);

  return (
    <div className="animate-fade-in">
      <div className="bg-white border border-zinc-200 rounded-md p-6 mb-6">
        <div className="flex flex-col lg:flex-row gap-6 items-start">
          <div className="w-20 h-20 rounded-md bg-zinc-100 border border-zinc-200 grid place-items-center font-bold text-2xl text-zinc-600 overflow-hidden flex-shrink-0">
            {v.logo ? <img src={v.logo} alt={v.name} className="w-full h-full object-cover" /> : v.name[0]}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl lg:text-3xl font-bold font-outfit">{v.name}</h1>
              {v.verified && <BadgeCheck className="w-6 h-6 text-emerald-500" />}
            </div>
            <p className="text-zinc-500 mt-1">{v.tagline}</p>
            <div className="flex flex-wrap gap-x-5 gap-y-2 mt-3 text-sm text-zinc-600">
              <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {v.location}</span>
              <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Est. {v.founded_year}</span>
              <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> {v.company_size} employees</span>
              {v.website && <span className="flex items-center gap-1.5"><Globe className="w-3.5 h-3.5" /> <a href={v.website} target="_blank" rel="noreferrer" className="hover:text-[#6366F1]">{v.website.replace(/https?:\/\//, '')}</a></span>}
            </div>
            <div className="flex items-center gap-3 mt-3">
              <Badge variant="secondary">{v.category}</Badge>
              <div className="flex items-center gap-1.5">
                <Stars value={v.rating} size={16} />
                <span className="text-sm font-semibold">{v.rating}</span>
                <span className="text-xs text-zinc-500">({(v.reviews || []).length} reviews)</span>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2 w-full lg:w-48">
            {user?.role === "buyer" && (
              <>
                <Button onClick={() => navigate(`/rfq?vendor=${v.id}`)} className="bg-[#6366F1] hover:bg-[#4F46E5]" data-testid="profile-send-rfq"><Send className="w-4 h-4 mr-1.5" /> Send RFQ</Button>
                <Button variant="outline" onClick={toggleFav} data-testid="profile-save"><Bookmark className={`w-4 h-4 mr-1.5 ${fav ? "fill-[#6366F1] text-[#6366F1]" : ""}`} /> {fav ? "Saved" : "Save Vendor"}</Button>
                <Button variant="outline" onClick={() => toast.info("Meeting request sent to the vendor team")} data-testid="profile-meeting">Request Meeting</Button>
              </>
            )}
            {user?.role === "vendor" && (
              <Button variant="outline" onClick={() => navigate("/profile/edit")} data-testid="profile-edit-own">Edit profile</Button>
            )}
          </div>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="credentials" data-testid="tab-credentials">Credentials</TabsTrigger>
          <TabsTrigger value="case-studies" data-testid="tab-case-studies">Case Studies</TabsTrigger>
          <TabsTrigger value="team" data-testid="tab-team">Team</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="tab-reviews">Reviews</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6 space-y-6">
          <div className="bg-white border border-zinc-200 rounded-md p-6">
            <h3 className="font-semibold mb-3">About</h3>
            <p className="text-sm text-zinc-700 leading-relaxed">{v.description}</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-zinc-200 rounded-md p-6">
              <h3 className="font-semibold mb-3">Services offered</h3>
              <div className="flex flex-wrap gap-2">
                {(v.service_tags || []).map((s) => <Badge key={s} variant="outline">{s}</Badge>)}
              </div>
            </div>
            <div className="bg-white border border-zinc-200 rounded-md p-6">
              <h3 className="font-semibold mb-3">Industries served</h3>
              <div className="flex flex-wrap gap-2">
                {(v.industries || []).map((s) => <Badge key={s} variant="outline">{s}</Badge>)}
              </div>
            </div>
          </div>
          <div className="bg-white border border-zinc-200 rounded-md p-6">
            <h3 className="font-semibold mb-3">Geographic coverage</h3>
            <div className="flex flex-wrap gap-2">
              {(v.geographic_coverage || []).length === 0 && <span className="text-sm text-zinc-500">No regions listed</span>}
              {(v.geographic_coverage || []).map((g) => <Badge key={g} variant="outline"><Globe className="w-3 h-3 mr-1" />{g}</Badge>)}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="credentials" className="mt-6 space-y-4">
          {(v.certifications || []).map((c, i) => (
            <div key={i} className="bg-white border border-zinc-200 rounded-md p-4 flex items-center justify-between" data-testid="certification-item">
              <div>
                <div className="font-semibold text-sm">{c.name}</div>
                <div className="text-xs text-zinc-500 mt-0.5">Issued by {c.issuing_body} · Uploaded {formatDate(c.uploaded_at)}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-zinc-500">Expires</div>
                <div className="text-sm font-semibold">{formatDate(c.expiry_date)}</div>
              </div>
            </div>
          ))}
          <div className="bg-white border border-zinc-200 rounded-md p-4">
            <h3 className="font-semibold mb-3 text-sm">Government Registrations</h3>
            <div className="flex flex-wrap gap-2">
              {v.govt_registrations?.msme && <Badge>MSME</Badge>}
              {v.govt_registrations?.startup_india && <Badge>Startup India</Badge>}
              {v.govt_registrations?.dpiit && <Badge>DPIIT</Badge>}
              {(v.iso_standards || []).map((iso) => <Badge key={iso} variant="outline">{iso}</Badge>)}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="case-studies" className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(v.case_studies || []).map((cs, i) => (
            <div key={i} className="bg-white border border-zinc-200 rounded-md p-5" data-testid="case-study">
              <div className="text-xs uppercase tracking-wider text-[#6366F1] font-semibold">{cs.client_industry}</div>
              <div className="mt-2 text-sm font-semibold blur-sm select-none">●●●●● Client</div>
              <div className="mt-3 text-xs text-zinc-500">Challenge</div>
              <div className="text-sm">{cs.challenge}</div>
              <div className="mt-3 text-xs text-zinc-500">Solution</div>
              <div className="text-sm">{cs.solution}</div>
              <div className="mt-4 pt-4 border-t border-zinc-100">
                <div className="text-xs text-zinc-500">Outcome</div>
                <div className="text-lg font-bold font-outfit text-emerald-600">{cs.outcome_metric}</div>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="team" className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(v.team || []).map((t, i) => (
            <div key={i} className="bg-white border border-zinc-200 rounded-md p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#6366F1] text-white grid place-items-center font-semibold">{t.name[0]}</div>
              <div>
                <div className="font-semibold text-sm">{t.name}</div>
                <div className="text-xs text-zinc-500">{t.title}</div>
                <div className="text-xs text-zinc-400 font-mono mt-1">{t.email}</div>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="reviews" className="mt-6 space-y-3">
          {(v.reviews || []).length === 0 && <div className="text-center py-8 text-sm text-zinc-500">No reviews yet</div>}
          {(v.reviews || []).map((r) => (
            <div key={r.id} className="bg-white border border-zinc-200 rounded-md p-5" data-testid="review-item">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-sm">{r.buyer_name}</div>
                  <Stars value={r.rating} />
                </div>
                <div className="text-xs text-zinc-500">{formatDate(r.created_at)}</div>
              </div>
              <p className="text-sm text-zinc-700 mt-2">{r.comment}</p>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
